import type { paths } from '@cou/contracts';
import { adminApi, type ApiFailure } from '../api/admin-api';

/*
 * The API-11 settlement-tooling client (admin-ui.md → Settlement › Corrections queue + Manual
 * actions). The market-scoped manual score/void, the entry-level recompute/void, and the
 * corrections queue — every call gated on `contests:settle`. Separate from the API-24 metrics
 * client (the strip); this owns the workbench's actions.
 */

type CorrectionsBody =
  paths['/api/admin/settlement/corrections']['get']['responses'][200]['content']['application/json'];

/** One staged post-maturity correction awaiting ops review (GL-20). */
export type Correction = CorrectionsBody['corrections'][number];

type ContestMarketsBody =
  paths['/api/admin/settlement/contests/{id}/markets']['get']['responses'][200]['content']['application/json'];

/** One row of the per-contest correction grid (a market with its result and re-grade impact). */
export type ContestMarket = ContestMarketsBody['markets'][number];

type RescoreBody =
  paths['/api/admin/settlement/contests/{id}/rescore']['post']['responses'][200]['content']['application/json'];

/** One edited market result to apply. */
export interface RescoreEdit {
  readonly externalMarketId: string;
  readonly score: number | null;
  readonly voided: boolean;
}

export type ListContestMarketsResult = ({ ok: true } & ContestMarketsBody) | ApiFailure;
export type RescoreResult = { ok: true; result: RescoreBody } | ApiFailure;

type StagedMarketResult =
  paths['/api/admin/settlement/markets/{externalMarketId}/score']['post']['responses'][200]['content']['application/json'];

type AdminEntryDetail =
  paths['/api/admin/settlement/recompute']['post']['responses'][200]['content']['application/json'];

export type ListCorrectionsResult = ({ ok: true } & CorrectionsBody) | ApiFailure;
export type AcknowledgeResult = { ok: true; correction: Correction } | ApiFailure;
export type MarketActionResult = { ok: true; result: StagedMarketResult } | ApiFailure;
export type EntryActionResult = { ok: true; entry: AdminEntryDetail } | ApiFailure;

/** The staged post-maturity corrections queue (API-11 GET; corrections are never auto-applied). */
export async function listCorrections(adminToken: string): Promise<ListCorrectionsResult> {
  return adminApi.get<CorrectionsBody>(adminToken, '/api/admin/settlement/corrections');
}

/** Mark a correction reviewed (API-11 POST). A 404 means it is no longer staged. */
export async function acknowledgeCorrection(
  adminToken: string,
  externalMarketId: string,
): Promise<AcknowledgeResult> {
  const result = await adminApi.post<Correction>(
    adminToken,
    `/api/admin/settlement/corrections/${encodeURIComponent(externalMarketId)}/acknowledge`,
  );
  return result.ok ? { ok: true, correction: result.body } : result;
}

/**
 * Manually assert a market's verdict when the feed never delivers or delivers wrong (API-11 POST).
 * Race-proof: a 409 means the settlement worker already settled it — review the correction instead.
 */
export async function manualScore(
  adminToken: string,
  externalMarketId: string,
  score: number,
  reason: string,
): Promise<MarketActionResult> {
  const result = await adminApi.post<StagedMarketResult>(
    adminToken,
    `/api/admin/settlement/markets/${encodeURIComponent(externalMarketId)}/score`,
    { score, reason },
  );
  return result.ok ? { ok: true, result: result.body } : result;
}

/** Void a market; affected Picks grade per the snapshotted void_policy (API-11 POST, GL-09). */
export async function manualVoid(
  adminToken: string,
  externalMarketId: string,
  reason: string,
): Promise<MarketActionResult> {
  const result = await adminApi.post<StagedMarketResult>(
    adminToken,
    `/api/admin/settlement/markets/${encodeURIComponent(externalMarketId)}/void`,
    { reason },
  );
  return result.ok ? { ok: true, result: result.body } : result;
}

/** Recompute an Entry's counters idempotently (API-11 POST); 404 when no such Entry. */
export async function recomputeEntry(
  adminToken: string,
  entryId: string,
): Promise<EntryActionResult> {
  const result = await adminApi.post<AdminEntryDetail>(
    adminToken,
    '/api/admin/settlement/recompute',
    { entryId },
  );
  return result.ok ? { ok: true, entry: result.body } : result;
}

/**
 * Void an Entry (`pickId` null) or a single Pick with a reason (API-11 POST). The Entry goes to the
 * terminal Voided state; a single Pick becomes VOID and the Entry's counters recompute.
 */
export async function voidEntryOrPick(
  adminToken: string,
  entryId: string,
  pickId: string | null,
  reason: string,
): Promise<EntryActionResult> {
  const result = await adminApi.post<AdminEntryDetail>(adminToken, '/api/admin/settlement/void', {
    entryId,
    pickId,
    reason,
  });
  return result.ok ? { ok: true, entry: result.body } : result;
}

/** The per-contest correction grid: every market with its current result and re-grade impact (API-11). */
export async function listContestMarkets(
  adminToken: string,
  contestId: string | null,
): Promise<ListContestMarketsResult> {
  // `null` is the "All contests" grid: every still-correctable Contest's markets in one list.
  return adminApi.get<ContestMarketsBody>(
    adminToken,
    contestId === null
      ? '/api/admin/settlement/markets'
      : `/api/admin/settlement/contests/${encodeURIComponent(contestId)}/markets`,
  );
}

/**
 * Apply edited market results and re-grade the affected not-yet-settled Entries (API-11, GL-20).
 * Returns the counts of markets applied, Entries re-graded, and settled Entries skipped.
 */
export async function rescoreContest(
  adminToken: string,
  contestId: string,
  edits: readonly RescoreEdit[],
  reason: string,
): Promise<RescoreResult> {
  const result = await adminApi.post<RescoreBody>(
    adminToken,
    `/api/admin/settlement/contests/${encodeURIComponent(contestId)}/rescore`,
    { edits, reason },
  );
  return result.ok ? { ok: true, result: result.body } : result;
}

export interface SettlementClient {
  listCorrections(adminToken: string): Promise<ListCorrectionsResult>;
  acknowledgeCorrection(adminToken: string, externalMarketId: string): Promise<AcknowledgeResult>;
  listContestMarkets(
    adminToken: string,
    contestId: string | null,
  ): Promise<ListContestMarketsResult>;
  rescoreContest(
    adminToken: string,
    contestId: string,
    edits: readonly RescoreEdit[],
    reason: string,
  ): Promise<RescoreResult>;
  manualScore(
    adminToken: string,
    externalMarketId: string,
    score: number,
    reason: string,
  ): Promise<MarketActionResult>;
  manualVoid(
    adminToken: string,
    externalMarketId: string,
    reason: string,
  ): Promise<MarketActionResult>;
  recomputeEntry(adminToken: string, entryId: string): Promise<EntryActionResult>;
  voidEntryOrPick(
    adminToken: string,
    entryId: string,
    pickId: string | null,
    reason: string,
  ): Promise<EntryActionResult>;
}

export const defaultSettlementClient: SettlementClient = {
  listCorrections,
  acknowledgeCorrection,
  manualScore,
  manualVoid,
  recomputeEntry,
  voidEntryOrPick,
  listContestMarkets,
  rescoreContest,
};
